import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { BOHOL_MUNICIPALITIES } from '../data/boholLocations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from './ui/select';
import { Badge } from './ui/badge';
import { ImageIcon, Upload, X, Star, AlertCircle } from 'lucide-react';

const MAX_PHOTOS = 5;
const MAX_RAW_BYTES = 20 * 1024 * 1024; // 20MB pre-compression (mirrors BannerCreateModal)
const MAX_SERVICE_AREAS = 20;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const RATE_TYPES = ['hourly', 'per_project', 'per_service', 'negotiable'];

/**
 * Create/edit form for a Pro service listing (spec §6.1/§6.2, §7.1). Photos are pre-uploaded
 * to /api/upload-image (returns {image_url, filename}) and then passed to the listing endpoints
 * as photos: [{url, path}]. The first photo is the primary/OG image.
 *
 * Contact details (phone/email) must NEVER be typed into title/description — the backend rejects
 * them (400) so the reveal flow stays the single source of a Pro's phone (§10.3/§11).
 *
 * @param {object} [initial] - existing listing for edit mode (prefill); omit for create mode.
 * @param {'create'|'edit'} mode
 * @param {(result: {listing: object, published: boolean}) => void} onSuccess
 */
const ProListingForm = ({ initial, mode = 'create', onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const isEdit = mode === 'edit';

  const [categoriesGrouped, setCategoriesGrouped] = useState({});
  const [categoryById, setCategoryById] = useState({});
  const [categoriesError, setCategoriesError] = useState(false);

  const [title, setTitle] = useState(initial?.title || '');
  const [categoryId, setCategoryId] = useState(initial?.category_id != null ? String(initial.category_id) : '');
  const [description, setDescription] = useState(initial?.description || '');
  const [serviceAreas, setServiceAreas] = useState(
    Array.isArray(initial?.service_area) ? initial.service_area : []
  );
  const [rateType, setRateType] = useState(initial?.rate_type || 'hourly');
  const [rateMin, setRateMin] = useState(initial?.rate_min != null ? String(initial.rate_min) : '');
  const [rateMax, setRateMax] = useState(initial?.rate_max != null ? String(initial.rate_max) : '');
  const [availabilityNote, setAvailabilityNote] = useState(initial?.availability_note || '');
  const [experienceYears, setExperienceYears] = useState(
    initial?.experience_years != null ? String(initial.experience_years) : ''
  );
  // photos: [{ url, path }] — url is the public image_url, path is the storage filename.
  const [photos, setPhotos] = useState(
    Array.isArray(initial?.photos) ? initial.photos.filter((p) => p && p.url) : []
  );

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Load the grouped category taxonomy (also used for the verification hint).
  const [categoriesReloadKey, setCategoriesReloadKey] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/service-categories');
        if (cancelled) return;
        const grouped = res.data?.grouped || {};
        const cats = res.data?.categories || [];
        setCategoriesGrouped(grouped);
        const byId = {};
        cats.forEach((c) => { byId[c.id] = c; });
        setCategoryById(byId);
        setCategoriesError(false);
      } catch (err) {
        if (!cancelled) {
          setCategoriesGrouped({});
          setCategoryById({});
          setCategoriesError(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [categoriesReloadKey]);

  const showRateInputs = rateType !== 'negotiable';

  const toggleServiceArea = (municipality) => {
    setServiceAreas((prev) => {
      if (prev.includes(municipality)) {
        return prev.filter((m) => m !== municipality);
      }
      if (prev.length >= MAX_SERVICE_AREAS) {
        toast.error(t('services.maxServiceAreas', { count: MAX_SERVICE_AREAS }));
        return prev;
      }
      return [...prev, municipality];
    });
  };

  const handleFilesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input so re-selecting the same file re-triggers onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      toast.error(t('services.maxPhotos', { count: MAX_PHOTOS }));
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.error(t('services.maxPhotos', { count: MAX_PHOTOS }));
    }

    setUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      for (const file of toUpload) {
        const fileName = file.name?.toLowerCase() || '';
        const hasValidExtension = /\.(jpe?g|png|webp)$/i.test(fileName);
        if (!ALLOWED_TYPES.includes(file.type) && !(file.type === '' && hasValidExtension)) {
          toast.error(t('services.errorImageType'));
          continue;
        }
        if (file.size > MAX_RAW_BYTES) {
          toast.error(t('services.errorImageSize'));
          continue;
        }
        let uploadFile = file;
        try {
          uploadFile = await compressImage(file, 'jobImage');
        } catch {
          uploadFile = file;
        }
        const form = new FormData();
        form.append('file', uploadFile, uploadFile.name || 'photo.jpg');
        try {
          const res = await api.post('/api/upload-image', form, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            timeout: UPLOAD_TIMEOUT,
          });
          const url = res.data?.image_url;
          const path = res.data?.filename;
          if (url) {
            setPhotos((prev) => (prev.length < MAX_PHOTOS ? [...prev, { url, path }] : prev));
          }
        } catch (err) {
          const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
          toast.error(detail || t('services.errorUploadFailed'));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const makePrimary = (index) => {
    setPhotos((prev) => {
      if (index <= 0 || index >= prev.length) return prev;
      const next = [...prev];
      const [picked] = next.splice(index, 1);
      return [picked, ...next];
    });
  };

  const buildPayload = () => {
    const payload = {
      title: title.trim(),
      category_id: categoryId,
      description: description.trim(),
      service_area: serviceAreas,
      rate_type: rateType,
      availability_note: availabilityNote.trim() || null,
      experience_years: experienceYears === '' ? null : Number(experienceYears),
      photos,
    };
    if (showRateInputs) {
      payload.rate_min = rateMin === '' ? null : Number(rateMin);
      payload.rate_max = rateMax === '' ? null : Number(rateMax);
    } else {
      payload.rate_min = null;
      payload.rate_max = null;
    }
    return payload;
  };

  const validateForPublish = () => {
    if (!title.trim()) return t('services.errorTitleRequired');
    if (!categoryId) return t('services.errorCategoryRequired');
    if (!description.trim()) return t('services.errorDescriptionRequired');
    if (serviceAreas.length === 0) return t('services.errorServiceAreaRequired');
    if (showRateInputs && rateMin !== '' && rateMax !== '' && Number(rateMin) > Number(rateMax)) {
      return t('services.errorRateOrder');
    }
    return '';
  };

  const mapApiError = (err) => {
    const status = err?.response?.status;
    const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
    // 400 (contact details / validation) and 409 (active cap) carry a friendly server-side
    // message — surface it verbatim. (403 is kept generic for any future auth edge.)
    if (status === 400 || status === 403 || status === 409) {
      return detail || t('services.errorGeneric');
    }
    if (status === 404) return t('services.errorNotFound');
    return detail || t('services.errorGeneric');
  };

  const submit = async (publish) => {
    if (submitting || uploading) return;
    setErrorMessage('');

    if (publish || isEdit) {
      // Publish + edit require the full field set (service area etc).
      const validationError = validateForPublish();
      if (validationError) {
        setErrorMessage(validationError);
        return;
      }
    } else {
      // Even a draft needs title/category/description — the backend model requires them,
      // so guide the user here rather than surfacing a raw 422.
      if (!title.trim()) { setErrorMessage(t('services.errorTitleRequired')); return; }
      if (!categoryId) { setErrorMessage(t('services.errorCategoryRequired')); return; }
      if (!description.trim()) { setErrorMessage(t('services.errorDescriptionRequired')); return; }
    }
    if (showRateInputs && rateMin !== '' && rateMax !== '' && Number(rateMin) > Number(rateMax)) {
      setErrorMessage(t('services.errorRateOrder'));
      return;
    }

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      const headers = { Authorization: `Bearer ${session.access_token}` };
      const payload = buildPayload();

      let listing;
      if (isEdit) {
        const res = await api.put(`/api/pro-listings/${initial.id}`, payload, { headers });
        listing = res.data?.listing;
        toast.success(t('services.saveSuccess'));
      } else {
        const res = await api.post('/api/pro-listings', { ...payload, publish }, { headers });
        listing = res.data?.listing;
        toast.success(publish ? t('services.publishSuccess') : t('services.draftSuccess'));
      }
      if (onSuccess) {
        onSuccess({ listing, published: isEdit ? (initial?.status === 'active') : publish });
      }
    } catch (err) {
      const message = mapApiError(err);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const groupNames = useMemo(() => Object.keys(categoriesGrouped), [categoriesGrouped]);
  const busy = submitting || uploading;

  // Enter / implicit submit maps to the primary action: publish on create, save on edit.
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    submit(isEdit ? false : true);
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="listing-title">{t('services.formTitle')}</Label>
        <Input
          id="listing-title"
          value={title}
          maxLength={120}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('services.formTitlePlaceholder')}
        />
      </div>

      {/* Category (grouped) */}
      <div className="space-y-2">
        <Label htmlFor="listing-category">{t('services.formCategory')}</Label>
        <Select value={categoryId || undefined} onValueChange={setCategoryId}>
          <SelectTrigger id="listing-category" aria-label={t('services.formCategory')}>
            <SelectValue placeholder={t('services.formCategoryPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {groupNames.map((group) => (
              <SelectGroup key={group}>
                <SelectLabel>{group}</SelectLabel>
                {(categoriesGrouped[group] || []).map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        {categoriesError && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <span className="flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0" aria-hidden="true" />
              {t('services.errorGeneric')}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setCategoriesReloadKey((k) => k + 1)}
            >
              {t('services.retry')}
            </Button>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="listing-description">{t('services.formDescription')}</Label>
        <Textarea
          id="listing-description"
          value={description}
          maxLength={2000}
          rows={5}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('services.formDescriptionPlaceholder')}
        />
        <p className="text-xs text-muted-foreground">{t('services.formNoContactHint')}</p>
      </div>

      {/* Service areas (multi-select checkbox list) */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium leading-none">
          {t('services.formServiceAreas')}
          {serviceAreas.length > 0 ? ` (${serviceAreas.length}/${MAX_SERVICE_AREAS})` : ''}
        </legend>
        <p className="text-xs text-muted-foreground">{t('services.formServiceAreasHint')}</p>
        <div className="max-h-64 overflow-y-auto rounded-md border border-input p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {BOHOL_MUNICIPALITIES.map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm cursor-pointer py-1.5 min-h-[36px]">
              <Checkbox
                checked={serviceAreas.includes(m)}
                onCheckedChange={() => toggleServiceArea(m)}
              />
              <span className="truncate" title={m}>{m}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Rate type + conditional min/max */}
      <div className="space-y-2">
        <Label>{t('services.formRateType')}</Label>
        <RadioGroup value={rateType} onValueChange={setRateType} className="grid grid-cols-2 gap-2">
          {RATE_TYPES.map((rt) => (
            <label
              key={rt}
              htmlFor={`rate-${rt}`}
              className={`flex items-center gap-2 rounded-lg border p-3 text-sm cursor-pointer ${rateType === rt ? 'border-primary bg-primary/5' : 'border-border'}`}
            >
              <RadioGroupItem value={rt} id={`rate-${rt}`} />
              {t(`services.rateType_${rt}`)}
            </label>
          ))}
        </RadioGroup>
        {showRateInputs && (
          <div className="flex items-center gap-3 pt-1">
            <div className="flex-1 space-y-1">
              <Label htmlFor="rate-min" className="text-xs">
                {t('services.formRateMin')}{' '}
                <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
              </Label>
              <Input
                id="rate-min"
                type="number"
                min="0"
                value={rateMin}
                onChange={(e) => setRateMin(e.target.value)}
                placeholder="₱"
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="rate-max" className="text-xs">
                {t('services.formRateMax')}{' '}
                <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
              </Label>
              <Input
                id="rate-max"
                type="number"
                min="0"
                value={rateMax}
                onChange={(e) => setRateMax(e.target.value)}
                placeholder="₱"
              />
            </div>
          </div>
        )}
      </div>

      {/* Availability + experience */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="listing-availability">
            {t('services.formAvailability')}{' '}
            <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
          </Label>
          <Input
            id="listing-availability"
            value={availabilityNote}
            maxLength={200}
            onChange={(e) => setAvailabilityNote(e.target.value)}
            placeholder={t('services.formAvailabilityPlaceholder')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="listing-experience">
            {t('services.formExperience')}{' '}
            <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
          </Label>
          <Input
            id="listing-experience"
            type="number"
            min="0"
            max="80"
            value={experienceYears}
            onChange={(e) => setExperienceYears(e.target.value)}
            placeholder={t('services.formExperiencePlaceholder')}
          />
        </div>
      </div>

      {/* Photos */}
      <div className="space-y-2">
        <Label>
          {t('services.formPhotos')}{' '}
          <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
          {photos.length > 0 ? ` (${photos.length}/${MAX_PHOTOS})` : ''}
        </Label>
        <p className="text-xs text-muted-foreground">{t('services.formPhotosHint')}</p>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((p, i) => (
              <div key={`${p.url}-${i}`} className="relative group rounded-lg overflow-hidden border border-border aspect-square">
                <img src={p.url} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <Badge className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0">
                    <Star size={10} className="mr-0.5" aria-hidden="true" />
                    {t('services.primaryPhoto')}
                  </Badge>
                )}
                {/* Remove (destructive) — top-right, away from make-primary to avoid mis-taps */}
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  title={t('services.removePhoto')}
                  aria-label={t('services.removePhoto')}
                  className="absolute top-1 right-1 rounded-full bg-black/60 p-2.5 text-white hover:bg-black/80 active:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <X size={16} aria-hidden="true" />
                </button>
                {/* Make primary — bottom-right */}
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={() => makePrimary(i)}
                    title={t('services.makePrimary')}
                    aria-label={t('services.makePrimary')}
                    className="absolute bottom-1 right-1 rounded-full bg-black/60 p-2.5 text-white hover:bg-black/80 active:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <Star size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {photos.length < MAX_PHOTOS && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleFilesSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
            >
              {uploading ? (
                <>{t('services.uploading')}</>
              ) : photos.length === 0 ? (
                <><ImageIcon className="mr-2 h-4 w-4" />{t('services.addPhotos')}</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" />{t('services.addMorePhotos')}</>
              )}
            </Button>
          </>
        )}
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-2">
        {isEdit ? (
          <Button type="submit" disabled={busy} className="flex-1 min-w-[140px]">
            {submitting ? t('services.saving') : t('services.saveListing')}
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => submit(false)}
              disabled={busy}
              className="flex-1 min-w-[140px]"
            >
              {submitting ? t('services.saving') : t('services.saveDraft')}
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="flex-1 min-w-[140px]"
            >
              {submitting ? t('services.publishing') : t('services.publishListing')}
            </Button>
          </>
        )}
      </div>
    </form>
  );
};

export default ProListingForm;
